import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Users, Share2, Gift, Star, Sparkles, Send,
  TrendingUp, Heart, PartyPopper, CheckCircle2, AlertCircle, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RESPONSIBILITIES = [
  'Share the festival with friends, followers, and your community',
  'Promote our events, partners, and experiences online and in person',
  'Help us grow the Aloha Tequila & Arts Festival family'
];

const REWARDS = [
  { icon: Gift, text: 'Rewards and bonuses for your referrals and results' },
  { icon: Star, text: 'Exclusive festival perks and insider access' },
  { icon: TrendingUp, text: 'Performance-based benefits — the more you spread the word, the more you earn' }
];

const AgaveAmbassadorsSection = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    instagram_handle: '',
    audience_size: '',
    why_message: ''
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_URL}/api/applications/ambassador`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          instagram_handle: form.instagram_handle.trim(),
          audience_size: form.audience_size.trim(),
          why_message: form.why_message.trim()
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Submission failed');
      }
      setStatus('success');
      if (typeof window !== 'undefined' && window.__atfTrackLead) {
        window.__atfTrackLead('ambassador_application_submit', {
          has_instagram: form.instagram_handle.trim() ? 'yes' : 'no'
        });
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(typeof err.message === 'string' ? err.message : 'Please try again or email everything@8eight8.net.');
    }
  };

  return (
    <section
      id="ambassadors"
      className="py-14 sm:py-20 bg-gradient-to-b from-gray-950 to-gray-900 relative overflow-hidden"
      data-testid="ambassadors-section"
    >
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-pink-500/5 rounded-full blur-3xl hidden sm:block" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl hidden sm:block" />

      <div className="max-w-4xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 backdrop-blur-sm px-4 py-2 rounded-full border border-orange-500/30 mb-4">
            <Users className="w-4 h-4 text-orange-400" />
            <span className="text-orange-400 font-bold text-xs sm:text-sm tracking-wider">JOIN THE TEAM</span>
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-white mb-3 sm:mb-4">
            Agave Ambassadors Wanted
          </h2>
          <p className="text-white/70 text-sm sm:text-lg max-w-2xl mx-auto px-2">
            Love tequila and good vibes? Help us spread the word about Aloha Tequila & Arts Festival 2026.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8">
          <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-4 sm:p-6 rounded-2xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center">
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h3 className="text-base sm:text-xl font-bold text-white">What you&apos;ll do</h3>
            </div>
            <ul className="space-y-2 sm:space-y-3">
              {RESPONSIBILITIES.map((item) => (
                <li key={item} className="flex items-start gap-2 sm:gap-3 text-white/80 text-xs sm:text-sm">
                  <Sparkles className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border-orange-500/20 p-4 sm:p-6 rounded-2xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h3 className="text-base sm:text-xl font-bold text-white">What you&apos;ll earn</h3>
            </div>
            <ul className="space-y-2 sm:space-y-3">
              {REWARDS.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.text} className="flex items-start gap-2 sm:gap-3 text-white/80 text-xs sm:text-sm">
                    <Icon className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                    <span>{item.text}</span>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>

        {/* Application form OR success state */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-5 sm:p-8 rounded-2xl">
          {status === 'success' ? (
            <div className="text-center py-6" data-testid="ambassador-success">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h3 className="text-xl sm:text-2xl font-black text-white mb-2">Mahalo! Application received.</h3>
              <p className="text-white/75 text-sm sm:text-base max-w-md mx-auto">
                We&apos;ll review your application and reach out within 5–7 business days at the email you provided.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-5">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Heart className="w-5 h-5 text-pink-400 animate-pulse" />
                  <PartyPopper className="w-5 h-5 text-orange-400" />
                </div>
                <h3 className="text-lg sm:text-xl font-black text-white">Apply now — takes 60 seconds</h3>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="ambassador-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amb-name" className="text-white/85 text-xs sm:text-sm mb-1.5 block">Full name *</Label>
                    <Input
                      id="amb-name"
                      data-testid="ambassador-input-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={update('name')}
                      placeholder="Leilani Kahale"
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/35"
                    />
                  </div>
                  <div>
                    <Label htmlFor="amb-email" className="text-white/85 text-xs sm:text-sm mb-1.5 block">Email *</Label>
                    <Input
                      id="amb-email"
                      data-testid="ambassador-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update('email')}
                      placeholder="you@example.com"
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/35"
                    />
                  </div>
                  <div>
                    <Label htmlFor="amb-instagram" className="text-white/85 text-xs sm:text-sm mb-1.5 block">Instagram handle</Label>
                    <Input
                      id="amb-instagram"
                      data-testid="ambassador-input-instagram"
                      type="text"
                      value={form.instagram_handle}
                      onChange={update('instagram_handle')}
                      placeholder="@yourhandle"
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/35"
                    />
                  </div>
                  <div>
                    <Label htmlFor="amb-audience" className="text-white/85 text-xs sm:text-sm mb-1.5 block">Audience size</Label>
                    <Input
                      id="amb-audience"
                      data-testid="ambassador-input-audience"
                      type="text"
                      value={form.audience_size}
                      onChange={update('audience_size')}
                      placeholder="e.g. 2.5K followers"
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/35"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="amb-phone" className="text-white/85 text-xs sm:text-sm mb-1.5 block">Phone (optional)</Label>
                    <Input
                      id="amb-phone"
                      data-testid="ambassador-input-phone"
                      type="tel"
                      value={form.phone}
                      onChange={update('phone')}
                      placeholder="(808) 555-0100"
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/35"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="amb-why" className="text-white/85 text-xs sm:text-sm mb-1.5 block">Why are you a fit? *</Label>
                    <Textarea
                      id="amb-why"
                      data-testid="ambassador-input-why"
                      required
                      rows={4}
                      value={form.why_message}
                      onChange={update('why_message')}
                      placeholder="Tell us how you'd promote the festival — your community, channels, why tequila & Hawaii excite you."
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/35 resize-none"
                    />
                  </div>
                </div>

                {status === 'error' && (
                  <div
                    className="flex items-start gap-2 text-red-300 text-xs sm:text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3"
                    data-testid="ambassador-error"
                  >
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={status === 'submitting'}
                  data-testid="ambassador-submit"
                  className="w-full h-12 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold rounded-xl inline-flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100"
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Submitting…
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Apply to be an Ambassador
                    </>
                  )}
                </Button>

                <p className="text-white/45 text-[11px] sm:text-xs text-center">
                  We&apos;ll review and reach out within 5–7 business days. 21+ only.
                </p>
              </form>
            </>
          )}
        </Card>
      </div>
    </section>
  );
};

export default AgaveAmbassadorsSection;
